import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calendar, Users, BookOpen, User, Plus, ChevronDown, Menu, Search, UserPlus, X } from 'lucide-react';
import MaestroAthleteEnrollments from '../MaestroAthleteEnrollments';
import { useDashboard } from './DashboardContext';

export const MaestroDashboard = () => {
  const {
    activeMaestroTab, setActiveMaestroTab,
    maestroMobileMenuOpen, setMaestroMobileMenuOpen,
    maestroAthletes, maestroEvents,
    selectedAthletesForEnrollment, setSelectedAthletesForEnrollment,
    showEnrollmentModal, setShowEnrollmentModal,
    selectedEventForEnrollment, setSelectedEventForEnrollment,
    showCreateAthleteModal, setShowCreateAthleteModal,
    newAthleteData, setNewAthleteData,
    showDuplicateAthleteDialog, duplicateAthleteInfo,
    showDeletionRequestModal, setShowDeletionRequestModal,
    selectedEnrollmentForDeletion, setSelectedEnrollmentForDeletion,
    deletionReason, setDeletionReason,
    validatePassword, checkDuplicateAthleteAndCreate,
    resetAthleteForm, overwriteAthletePassword,
    enrollAthletesInEvent, requestEnrollmentDeletion,
    associateAthleteSearch, setAssociateAthleteSearch,
    associateAthleteResults, associateAthlete, transferConflict, setTransferConflict, confirmTransfer
  } = useDashboard();

  return (
    <>
      <Tabs value={activeMaestroTab} onValueChange={setActiveMaestroTab} className="space-y-6" data-testid="maestro-dashboard">
        <TabsList className="hidden md:grid w-full grid-cols-4">
          <TabsTrigger value="athletes" data-testid="maestro-athletes-tab">
            <Users className="w-4 h-4 mr-2" />
            I Miei Atleti ({maestroAthletes.length})
          </TabsTrigger>
          <TabsTrigger value="associate" data-testid="maestro-associate-tab">
            <UserPlus className="w-4 h-4 mr-2" />
            Associa Atleti
          </TabsTrigger>
          <TabsTrigger value="events" data-testid="maestro-events-tab">
            <Calendar className="w-4 h-4 mr-2" />
            Eventi Disponibili
          </TabsTrigger>
          <TabsTrigger value="enrollments" data-testid="maestro-enrollments-tab">
            <BookOpen className="w-4 h-4 mr-2" />
            Iscrizioni Atleti
          </TabsTrigger>
        </TabsList>

        {/* Mobile Menu */}
        <div className="md:hidden relative">
          <button
            onClick={() => setMaestroMobileMenuOpen(!maestroMobileMenuOpen)}
            className="w-full flex items-center justify-between px-4 py-3 bg-white border rounded-lg shadow-sm"
          >
            <span className="font-medium">
              {activeMaestroTab === 'athletes' ? 'I Miei Atleti' : 
               activeMaestroTab === 'events' ? 'Eventi Disponibili' : 'Iscrizioni Atleti'}
            </span>
            <ChevronDown className={`w-5 h-5 transition-transform ${maestroMobileMenuOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {maestroMobileMenuOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg">
              {[
                { value: 'athletes', label: `I Miei Atleti (${maestroAthletes.length})` },
                { value: 'events', label: 'Eventi Disponibili' },
                { value: 'enrollments', label: 'Iscrizioni Atleti' }
              ].map(item => (
                <button
                  key={item.value}
                  onClick={() => { setActiveMaestroTab(item.value); setMaestroMobileMenuOpen(false); }}
                  className={`w-full px-4 py-3 text-left ${activeMaestroTab === item.value ? 'bg-red-50 text-red-600' : ''}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Athletes Tab */}
        <TabsContent value="athletes" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>I Miei Atleti</CardTitle>
                <CardDescription>Gestisci gli atleti sotto la tua responsabilità</CardDescription>
              </div>
              <Button 
                onClick={() => setShowCreateAthleteModal(true)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="create-athlete-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Atleta
              </Button>
            </CardHeader>
            <CardContent>
              {maestroAthletes.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  Non hai ancora atleti. Clicca "Nuovo Atleta" per crearne uno.
                </p>
              ) : (
                <div className="space-y-3">
                  {maestroAthletes.map((athlete) => (
                    <div key={athlete.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <p className="font-semibold">{athlete.name} {athlete.surname}</p>
                          <p className="text-sm text-gray-500">{athlete.email}</p>
                          {athlete.athlete_number && (
                            <Badge variant="outline" className="mt-1">N° {athlete.athlete_number}</Badge>
                          )}
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={selectedAthletesForEnrollment.includes(athlete.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedAthletesForEnrollment([...selectedAthletesForEnrollment, athlete.id]);
                          } else {
                            setSelectedAthletesForEnrollment(selectedAthletesForEnrollment.filter(id => id !== athlete.id));
                          }
                        }}
                        className="w-5 h-5 rounded border-gray-300"
                      />
                    </div>
                  ))}
                  
                  {selectedAthletesForEnrollment.length > 0 && (
                    <div className="flex justify-end mt-4">
                      <Button onClick={() => setShowEnrollmentModal(true)} className="bg-blue-600 hover:bg-blue-700">
                        Iscrivi {selectedAthletesForEnrollment.length} atleti a evento
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        {/* Associate Athletes Tab */}
        <TabsContent value="associate" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Associa Atleti alla tua Scuola
              </CardTitle>
              <CardDescription>Cerca un atleta per nome o cognome e associalo alla tua scuola</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative mb-4">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      value={associateAthleteSearch}
                      onChange={(e) => setAssociateAthleteSearch(e.target.value)}
                      placeholder="Cerca atleta per nome o cognome..."
                      className="pl-9"
                      data-testid="associate-athlete-search"
                    />
                  </div>
                </div>
              </div>
              {associateAthleteResults.length > 0 ? (
                <div className="space-y-2" data-testid="associate-athlete-results">
                  {associateAthleteResults.map(athlete => (
                    <div key={athlete.id} className="flex items-center justify-between border rounded-lg p-3 hover:bg-gray-50">
                      <div>
                        <p className="font-medium">{athlete.name} {athlete.surname}</p>
                        <p className="text-sm text-gray-500">{athlete.email}</p>
                        {athlete.maestro_id && (
                          <p className="text-xs text-orange-600">Già associato a un maestro</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => associateAthlete(athlete.id)}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                        data-testid={`associate-btn-${athlete.id}`}
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        Associa
                      </Button>
                    </div>
                  ))}
                </div>
              ) : associateAthleteSearch.length >= 2 ? (
                <p className="text-gray-500 text-center py-4">Nessun atleta trovato</p>
              ) : (
                <p className="text-gray-400 text-center py-4">Digita almeno 2 caratteri per cercare</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        {/* Events Tab */}
        <TabsContent value="events" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Eventi Disponibili</CardTitle>
              <CardDescription>Eventi a cui puoi iscrivere i tuoi atleti</CardDescription>
            </CardHeader>
            <CardContent>
              {maestroEvents.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Nessun evento disponibile al momento.</p>
              ) : (
                <div className="space-y-4">
                  {maestroEvents.map((event) => (
                    <div key={event.id} className="p-4 border rounded-lg hover:border-red-300 transition-colors">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold text-lg">{event.title}</h3>
                          <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <Badge variant="outline">
                              {event.type === 'stage' ? 'Stage' : event.type === 'lecture' ? 'Lecture' : event.type === 'online' ? 'Online' : event.type}
                            </Badge>
                            <Badge variant="secondary">
                              <Calendar className="w-3 h-3 mr-1" />
                              {event.date_tbd ? 'DATA DA DEFINIRE' : new Date(event.start_date).toLocaleDateString('it-IT')}
                            </Badge>
                            {event.location && <Badge variant="secondary">{event.location}</Badge>}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => { setSelectedEventForEnrollment(event.id); setShowEnrollmentModal(true); }}
                          disabled={selectedAthletesForEnrollment.length === 0}
                        >
                          Iscrivi Atleti
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Enrollments Tab */}
        <TabsContent value="enrollments" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Iscrizioni dei Miei Atleti</CardTitle>
              <CardDescription>Visualizza e gestisci le iscrizioni</CardDescription>
            </CardHeader>
            <CardContent>
              {maestroAthletes.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Non hai atleti da gestire.</p>
              ) : (
                <div className="space-y-6">
                  {maestroAthletes.map((athlete) => (
                    <MaestroAthleteEnrollments 
                      key={athlete.id}
                      athlete={athlete}
                      onRequestDeletion={(enrollment) => {
                        setSelectedEnrollmentForDeletion({
                          ...enrollment,
                          athlete_name: `${athlete.name} ${athlete.surname}`
                        });
                        setShowDeletionRequestModal(true);
                      }}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Athlete Modal */}
      {showCreateAthleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Crea Nuovo Atleta</CardTitle>
              <CardDescription>L'atleta sarà automaticamente sotto la tua gestione</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={checkDuplicateAthleteAndCreate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="new-athlete-name">Nome *</Label>
                    <Input id="new-athlete-name" value={newAthleteData.name}
                      onChange={(e) => setNewAthleteData({...newAthleteData, name: e.target.value})} required data-testid="new-athlete-name" />
                  </div>
                  <div>
                    <Label htmlFor="new-athlete-surname">Cognome *</Label>
                    <Input id="new-athlete-surname" value={newAthleteData.surname}
                      onChange={(e) => setNewAthleteData({...newAthleteData, surname: e.target.value})} required data-testid="new-athlete-surname" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="new-athlete-email">Email *</Label>
                  <Input id="new-athlete-email" type="email" value={newAthleteData.email}
                    onChange={(e) => setNewAthleteData({...newAthleteData, email: e.target.value})} required data-testid="new-athlete-email" />
                </div>
                <div>
                  <Label htmlFor="new-athlete-password">Password *</Label>
                  <Input id="new-athlete-password" type="password" value={newAthleteData.password}
                    onChange={(e) => setNewAthleteData({...newAthleteData, password: e.target.value})} required data-testid="new-athlete-password" />
                  <p className="text-xs text-gray-500 mt-1">Minimo 8 caratteri, 1 numero, 1 carattere speciale</p>
                  {newAthleteData.password && (() => {
                    const v = validatePassword(newAthleteData.password);
                    return (
                      <div className="mt-2 space-y-1">
                        <p className={`text-xs ${v.minLength ? 'text-green-600' : 'text-red-600'}`}>
                          {v.minLength ? '\u2713' : '\u2717'} Almeno 8 caratteri
                        </p>
                        <p className={`text-xs ${v.hasNumber ? 'text-green-600' : 'text-red-600'}`}>
                          {v.hasNumber ? '\u2713' : '\u2717'} Almeno 1 numero
                        </p>
                        <p className={`text-xs ${v.hasSpecial ? 'text-green-600' : 'text-red-600'}`}>
                          {v.hasSpecial ? '\u2713' : '\u2717'} Almeno 1 carattere speciale
                        </p>
                      </div>
                    );
                  })()}
                </div>
                
                <div className="border-t pt-4">
                  <Label htmlFor="payment-pdf" className="text-sm font-semibold text-gray-800">Copia Pagamento (PDF) *</Label>
                  <p className="text-xs text-gray-500 mb-2">Carica il PDF della ricevuta di pagamento (obbligatorio)</p>
                  <Input id="payment-pdf" type="file" accept=".pdf"
                    onChange={(e) => setNewAthleteData({...newAthleteData, payment_pdf: e.target.files?.[0] || null})}
                    required data-testid="payment-pdf-input" className="cursor-pointer" />
                  {newAthleteData.payment_pdf && (
                    <p className="text-xs text-green-600 mt-1">✓ File selezionato: {newAthleteData.payment_pdf.name}</p>
                  )}
                </div>

                <div className="pt-2">
                  <div className="flex items-start space-x-3">
                    <input type="checkbox" id="payment-confirmed" checked={newAthleteData.payment_confirmed}
                      onChange={(e) => setNewAthleteData({...newAthleteData, payment_confirmed: e.target.checked})}
                      className="mt-1 w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500" required data-testid="payment-confirmed-checkbox" />
                    <div>
                      <Label htmlFor="payment-confirmed" className="text-sm font-semibold text-gray-800">Pagamento Avvenuto *</Label>
                      <p className="text-xs text-gray-500">Confermo che il pagamento per questo atleta è stato ricevuto</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="outline" onClick={resetAthleteForm}>Annulla</Button>
                  <Button type="submit" className="bg-green-600 hover:bg-green-700"
                    disabled={!newAthleteData.payment_confirmed || !newAthleteData.payment_pdf} data-testid="create-athlete-submit">
                    Crea Atleta
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Duplicate Athlete Dialog */}
      {showDuplicateAthleteDialog && duplicateAthleteInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-orange-600">Attenzione: Atleta Esistente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-gray-700">Il profilo per il quale stai lavorando esiste già:</p>
                  <p className="font-semibold mt-2 text-lg">{duplicateAthleteInfo.name} {duplicateAthleteInfo.surname}</p>
                  <p className="text-sm text-gray-500">{duplicateAthleteInfo.email}</p>
                </div>
                <p className="text-gray-600">Vuoi sovrascrivere la password o annullare l'operazione?</p>
                <div className="flex justify-end space-x-3 pt-4">
                  <Button variant="outline" onClick={resetAthleteForm} data-testid="cancel-duplicate-btn">Annulla</Button>
                  <Button onClick={overwriteAthletePassword} className="bg-orange-600 hover:bg-orange-700" data-testid="overwrite-password-btn">
                    Sovrascrivi Password
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Enrollment Modal */}
      {showEnrollmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Iscrivi Atleti a Evento</CardTitle>
              <CardDescription>{selectedAthletesForEnrollment.length} atleti selezionati</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label>Seleziona Evento</Label>
                  <Select value={selectedEventForEnrollment || ''} onValueChange={setSelectedEventForEnrollment}>
                    <SelectTrigger><SelectValue placeholder="Scegli un evento" /></SelectTrigger>
                    <SelectContent>
                      {maestroEvents.map((event) => (
                        <SelectItem key={event.id} value={event.id}>
                          {event.title} - {event.date_tbd ? 'DATA DA DEFINIRE' : new Date(event.start_date).toLocaleDateString('it-IT')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm font-medium mb-2">Atleti da iscrivere:</p>
                  <div className="space-y-1">
                    {maestroAthletes.filter(a => selectedAthletesForEnrollment.includes(a.id)).map(a => (
                      <p key={a.id} className="text-sm text-gray-600">• {a.name} {a.surname}</p>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <Button variant="outline" onClick={() => { setShowEnrollmentModal(false); setSelectedEventForEnrollment(null); }}>Annulla</Button>
                  <Button onClick={enrollAthletesInEvent} disabled={!selectedEventForEnrollment} className="bg-blue-600 hover:bg-blue-700">Iscrivi</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Deletion Request Modal */}
      {showDeletionRequestModal && selectedEnrollmentForDeletion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Richiedi Eliminazione Iscrizione</CardTitle>
              <CardDescription>La richiesta sarà inviata all'amministratore per approvazione</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm"><strong>Atleta:</strong> {selectedEnrollmentForDeletion.athlete_name}</p>
                  <p className="text-sm"><strong>Evento:</strong> {selectedEnrollmentForDeletion.event_title}</p>
                </div>
                <div>
                  <Label htmlFor="deletion-reason">Motivo della richiesta *</Label>
                  <Textarea id="deletion-reason" value={deletionReason} onChange={(e) => setDeletionReason(e.target.value)}
                    placeholder="Inserisci il motivo per cui richiedi l'eliminazione..." rows={3} required />
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <Button variant="outline" onClick={() => { setShowDeletionRequestModal(false); setSelectedEnrollmentForDeletion(null); setDeletionReason(''); }}>
                    Annulla
                  </Button>
                  <Button onClick={requestEnrollmentDeletion} disabled={!deletionReason.trim()} className="bg-red-600 hover:bg-red-700">
                    Invia Richiesta
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Transfer Conflict Dialog */}
      {transferConflict && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="transfer-conflict-dialog">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-orange-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Atleta già associato</h3>
            </div>
            <p className="text-sm text-gray-700 mb-4">{transferConflict.message}</p>
            <p className="text-sm text-gray-600 mb-6">Vuoi trasferire l'atleta presso la tua scuola?</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setTransferConflict(null)} data-testid="cancel-transfer-btn">
                Annulla
              </Button>
              <Button className="flex-1 bg-orange-600 hover:bg-orange-700 text-white" onClick={() => confirmTransfer(transferConflict.athleteId)} data-testid="confirm-transfer-btn">
                Conferma Trasferimento
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
